import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  X, Plus, Trash2, FileText, Send, Loader2, Download, Package
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatBRL = (value) => {
  const v = Number(value || 0);
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

// Parse Brazilian price strings like "R$ 4.500,00" or "4500.00" into a number.
// Returns 0 when the string is non-numeric (e.g. "Consulte", "A combinar").
const parseBRLPrice = (priceStr) => {
  if (priceStr == null) return 0;
  if (typeof priceStr === 'number') return priceStr;
  const s = String(priceStr).replace(/[^\d,.\-]/g, '');
  if (!s) return 0;
  // Brazilian format: "4.500,00" → "4500.00"
  const normalized = s.includes(',')
    ? s.replace(/\./g, '').replace(',', '.')
    : s;
  const n = parseFloat(normalized);
  return isNaN(n) ? 0 : n;
};

const QuoteBuilder = ({ lead, onClose, showMessage, existingQuote }) => {
  const [products, setProducts] = useState([]);
  const [items, setItems] = useState(existingQuote?.items?.length ? existingQuote.items : [{ productName: '', quantity: 1, unitPrice: 0 }]);
  const [shippingCost, setShippingCost] = useState(existingQuote?.shippingCost || 0);
  const [discountType, setDiscountType] = useState(existingQuote?.discountType || 'value');
  const [discountValue, setDiscountValue] = useState(existingQuote?.discountValue || 0);
  const [discountPercent, setDiscountPercent] = useState(existingQuote?.discountPercent || 0);
  const [validityDays, setValidityDays] = useState(existingQuote?.validityDays || 7);
  const [paymentTerms, setPaymentTerms] = useState(existingQuote?.paymentTerms || 'Pix à vista (5% desc) ou 3x no cartão');
  const [observations, setObservations] = useState(existingQuote?.observations || 'Frete incluso via transportadora. Garantia de 1 ano contra defeitos de fabricação.');
  const [saving, setSaving] = useState(false);
  const [generated, setGenerated] = useState(existingQuote || null);

  useEffect(() => {
    axios.get(`${API}/products`).then(res => setProducts(res.data)).catch(() => {});
  }, []);

  const subtotal = items.reduce((acc, it) => acc + (Number(it.quantity || 0) * Number(it.unitPrice || 0)), 0);
  const discountResolved = discountType === 'percent'
    ? (subtotal + Number(shippingCost || 0)) * (Number(discountPercent || 0) / 100)
    : Number(discountValue || 0);
  const total = Math.max(0, subtotal + Number(shippingCost || 0) - discountResolved);

  const updateItem = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };
  const addItem = (preset) => {
    setItems(prev => [...prev, preset || { productName: '', quantity: 1, unitPrice: 0, custom: false }]);
  };
  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const handleProductSelect = (idx, value) => {
    if (value === '__custom__') {
      // Switch this row to free-text input
      setItems(prev => prev.map((it, i) => i === idx ? { ...it, productName: '', custom: true, unitPrice: 0 } : it));
      return;
    }
    if (value === '') {
      setItems(prev => prev.map((it, i) => i === idx ? { ...it, productName: '', custom: false, unitPrice: 0 } : it));
      return;
    }
    const product = products.find(p => p.id === value);
    if (!product) return;
    const parsedPrice = parseBRLPrice(product.price);
    setItems(prev => prev.map((it, i) => i === idx ? {
      ...it,
      productName: product.name,
      unitPrice: parsedPrice,
      custom: false
    } : it));
  };

  const addFromCatalog = (product) => {
    addItem({
      productName: product.name,
      quantity: 1,
      unitPrice: parseBRLPrice(product.price),
      custom: false
    });
  };

  const handleGenerate = async () => {
    if (items.length === 0 || items.every(i => !i.productName.trim())) {
      showMessage?.('Adicione pelo menos um produto', 'error');
      return;
    }
    setSaving(true);
    try {
      const cleanItems = items
        .filter(i => i.productName.trim())
        .map(i => ({
          productName: i.productName.trim(),
          quantity: Number(i.quantity) || 1,
          unitPrice: Number(i.unitPrice) || 0
        }));
      const payload = {
        leadId: lead?.id,
        leadName: lead?.name || 'Cliente',
        leadPhone: lead?.phone || '',
        leadCity: lead?.city || '',
        leadState: lead?.state || '',
        items: cleanItems,
        shippingCost: Number(shippingCost) || 0,
        discountType,
        discountValue: discountType === 'value' ? Number(discountValue) || 0 : 0,
        discountPercent: discountType === 'percent' ? Number(discountPercent) || 0 : 0,
        validityDays: Number(validityDays) || 7,
        paymentTerms,
        observations
      };
      const res = await axios.post(`${API}/quotes`, payload);
      setGenerated(res.data);
      showMessage?.(`Orçamento Nº ${res.data.number} gerado!`);
    } catch (err) {
      console.error('[QuoteBuilder] generate failed', err?.response?.data || err);
      const detail = err?.response?.data?.detail || 'Erro ao gerar orçamento';
      showMessage?.(detail, 'error');
    } finally {
      setSaving(false);
    }
  };

  const pdfUrl = generated ? `${API}/quotes/${generated.id}/pdf` : null;

  const handleSendWhatsApp = () => {
    if (!generated || !lead?.phone) {
      showMessage?.('Lead sem telefone cadastrado', 'error');
      return;
    }
    const cleanPhone = lead.phone.replace(/\D/g, '');
    const firstName = (lead.name || '').split(' ')[0];
    const numStr = String(generated.number).padStart(4, '0');
    const message = [
      `Olá ${firstName}! 🤠🔥`,
      '',
      'Aqui é da Rotativa Gaúcha. Conforme conversamos, segue o teu orçamento personalizado:',
      '',
      `📄 *Orçamento Nº ${numStr}*`,
      `💰 *Valor total:* ${formatBRL(generated.total)}`,
      `📅 *Validade:* ${generated.validityDays} dias`,
      '',
      `📥 PDF completo: ${pdfUrl}`,
      '',
      'Qualquer dúvida, é só chamar! Bah, vamos fechar negócio? 🔥'
    ].join('\n');
    const url = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        data-testid="quote-builder-modal"
        className="bg-stone-900 border-2 border-amber-500/40 rounded-2xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-stone-700 bg-gradient-to-r from-amber-600/20 to-orange-600/10 flex items-center justify-between">
          <div>
            <h3 className="text-xl font-bold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              {generated ? `Orçamento Nº ${String(generated.number).padStart(4, '0')}` : 'Novo Orçamento'}
            </h3>
            <p className="text-stone-400 text-sm">Cliente: <span className="text-amber-400 font-semibold">{lead?.name}</span></p>
          </div>
          <button
            data-testid="close-quote-builder"
            onClick={onClose}
            className="text-stone-400 hover:text-white p-1 rounded-full hover:bg-stone-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          {/* Quick add from catalog */}
          {products.length > 0 && !generated && (
            <div>
              <p className="text-stone-400 text-xs mb-2 flex items-center gap-1"><Package className="w-3 h-3" /> Adicionar do catálogo (preço auto-preenchido):</p>
              <div className="flex flex-wrap gap-2">
                {products.map(p => {
                  const parsed = parseBRLPrice(p.price);
                  return (
                    <button
                      key={p.id}
                      data-testid={`catalog-add-${p.id}`}
                      onClick={() => addFromCatalog(p)}
                      className="bg-stone-800 hover:bg-amber-500 hover:text-stone-900 text-stone-200 text-xs px-3 py-1.5 rounded-lg border border-stone-700 hover:border-amber-500 transition-colors flex items-center gap-1.5"
                      title={parsed > 0 ? `${p.name} — ${formatBRL(parsed)}` : p.name}
                    >
                      <Plus className="w-3 h-3" /> {p.name}
                      {parsed > 0 && (
                        <span className="text-amber-400 group-hover:text-stone-900 font-semibold">{formatBRL(parsed)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-white font-semibold text-sm">Itens do orçamento</h4>
              {!generated && (
                <button
                  data-testid="add-item-btn"
                  onClick={() => addItem()}
                  className="bg-amber-500 hover:bg-amber-400 text-stone-900 text-xs px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1"
                >
                  <Plus className="w-3.5 h-3.5" /> Adicionar
                </button>
              )}
            </div>

            <div className="space-y-2">
              {items.map((item, idx) => {
                // Match this item to a catalog product (when name matches)
                const matchedProduct = products.find(p => p.name === item.productName);
                const isCustom = item.custom || (item.productName && !matchedProduct);
                const selectValue = isCustom ? '__custom__' : (matchedProduct?.id || '');

                return (
                  <div key={idx} className="bg-stone-800 border border-stone-700 rounded-lg p-3 grid grid-cols-12 gap-2 items-center">
                    {isCustom ? (
                      <input
                        data-testid={`item-name-${idx}`}
                        type="text"
                        value={item.productName}
                        onChange={(e) => updateItem(idx, 'productName', e.target.value)}
                        placeholder="Nome do produto (custom)"
                        disabled={!!generated}
                        className="col-span-12 sm:col-span-6 bg-stone-900 border border-amber-500/40 text-white text-sm px-3 py-2 rounded-lg focus:border-amber-500 focus:outline-none disabled:opacity-60"
                      />
                    ) : (
                      <select
                        data-testid={`item-select-${idx}`}
                        value={selectValue}
                        onChange={(e) => handleProductSelect(idx, e.target.value)}
                        disabled={!!generated}
                        className="col-span-12 sm:col-span-6 bg-stone-900 border border-stone-600 text-white text-sm px-3 py-2 rounded-lg focus:border-amber-500 focus:outline-none disabled:opacity-60 cursor-pointer"
                      >
                        <option value="">— Selecionar produto —</option>
                        {products.map(p => {
                          const parsed = parseBRLPrice(p.price);
                          return (
                            <option key={p.id} value={p.id}>
                              {p.name}{parsed > 0 ? ` — ${formatBRL(parsed)}` : ''}
                            </option>
                          );
                        })}
                        <option value="__custom__">✏️ Outro (digitar manualmente)</option>
                      </select>
                    )}
                    <input
                      data-testid={`item-qty-${idx}`}
                      type="number"
                      min="1"
                      value={item.quantity}
                      onChange={(e) => updateItem(idx, 'quantity', e.target.value)}
                      placeholder="Qtd"
                      disabled={!!generated}
                      className="col-span-3 sm:col-span-2 bg-stone-900 border border-stone-600 text-white text-sm px-2 py-2 rounded-lg focus:border-amber-500 focus:outline-none disabled:opacity-60"
                    />
                    <input
                      data-testid={`item-price-${idx}`}
                      type="number"
                      min="0"
                      step="0.01"
                      value={item.unitPrice}
                      onChange={(e) => updateItem(idx, 'unitPrice', e.target.value)}
                      placeholder="R$ Unit."
                      disabled={!!generated}
                      className="col-span-7 sm:col-span-3 bg-stone-900 border border-stone-600 text-white text-sm px-3 py-2 rounded-lg focus:border-amber-500 focus:outline-none disabled:opacity-60"
                    />
                    {!generated && (
                      <button
                        data-testid={`remove-item-${idx}`}
                        onClick={() => removeItem(idx)}
                        disabled={items.length === 1}
                        className="col-span-2 sm:col-span-1 text-red-400 hover:bg-red-500/20 disabled:opacity-30 p-2 rounded-lg flex items-center justify-center"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Shipping & Discount */}
          {!generated && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-stone-400 text-xs block mb-1">Frete (R$)</label>
                <input
                  data-testid="shipping-input"
                  type="number"
                  min="0"
                  step="0.01"
                  value={shippingCost}
                  onChange={(e) => setShippingCost(e.target.value)}
                  className="w-full bg-stone-800 border border-stone-600 text-white text-sm px-3 py-2 rounded-lg focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-stone-400 text-xs block mb-1">Desconto</label>
                <div className="flex gap-2">
                  <select
                    value={discountType}
                    onChange={(e) => setDiscountType(e.target.value)}
                    className="bg-stone-800 border border-stone-600 text-white text-sm px-2 py-2 rounded-lg focus:border-amber-500 focus:outline-none"
                  >
                    <option value="value">R$</option>
                    <option value="percent">%</option>
                  </select>
                  <input
                    data-testid="discount-input"
                    type="number"
                    min="0"
                    step="0.01"
                    value={discountType === 'percent' ? discountPercent : discountValue}
                    onChange={(e) => discountType === 'percent' ? setDiscountPercent(e.target.value) : setDiscountValue(e.target.value)}
                    className="flex-1 bg-stone-800 border border-stone-600 text-white text-sm px-3 py-2 rounded-lg focus:border-amber-500 focus:outline-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-stone-400 text-xs block mb-1">Validade (dias)</label>
                <input
                  data-testid="validity-input"
                  type="number"
                  min="1"
                  value={validityDays}
                  onChange={(e) => setValidityDays(e.target.value)}
                  className="w-full bg-stone-800 border border-stone-600 text-white text-sm px-3 py-2 rounded-lg focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div>
                <label className="text-stone-400 text-xs block mb-1">Forma de pagamento</label>
                <input
                  type="text"
                  value={paymentTerms}
                  onChange={(e) => setPaymentTerms(e.target.value)}
                  className="w-full bg-stone-800 border border-stone-600 text-white text-sm px-3 py-2 rounded-lg focus:border-amber-500 focus:outline-none"
                />
              </div>
              <div className="sm:col-span-2">
                <label className="text-stone-400 text-xs block mb-1">Observações</label>
                <textarea
                  value={observations}
                  onChange={(e) => setObservations(e.target.value)}
                  rows={2}
                  className="w-full bg-stone-800 border border-stone-600 text-white text-sm px-3 py-2 rounded-lg focus:border-amber-500 focus:outline-none resize-none"
                />
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="bg-gradient-to-br from-amber-500/10 to-stone-900 border border-amber-500/30 rounded-xl p-4 space-y-1">
            <div className="flex justify-between text-stone-300 text-sm">
              <span>Subtotal</span>
              <span data-testid="subtotal-value">{formatBRL(subtotal)}</span>
            </div>
            {Number(shippingCost) > 0 && (
              <div className="flex justify-between text-stone-300 text-sm">
                <span>Frete</span>
                <span>{formatBRL(shippingCost)}</span>
              </div>
            )}
            {discountResolved > 0 && (
              <div className="flex justify-between text-red-400 text-sm">
                <span>Desconto{discountType === 'percent' ? ` (${discountPercent}%)` : ''}</span>
                <span>- {formatBRL(discountResolved)}</span>
              </div>
            )}
            <div className="border-t border-amber-500/30 mt-2 pt-2 flex justify-between items-center">
              <span className="text-white font-bold">TOTAL</span>
              <span data-testid="total-value" className="text-amber-400 text-2xl font-bold">{formatBRL(total)}</span>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 border-t border-stone-700 bg-stone-800/50 flex flex-col sm:flex-row gap-2">
          {!generated ? (
            <>
              <button
                data-testid="generate-quote-btn"
                onClick={handleGenerate}
                disabled={saving}
                className="flex-1 bg-amber-500 hover:bg-amber-400 disabled:opacity-50 text-stone-900 font-bold py-3 rounded-xl flex items-center justify-center gap-2"
              >
                {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
                Gerar Orçamento
              </button>
              <button
                onClick={onClose}
                className="bg-stone-700 hover:bg-stone-600 text-white font-medium px-6 py-3 rounded-xl"
              >
                Cancelar
              </button>
            </>
          ) : (
            <>
              <a
                data-testid="download-pdf-btn"
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-stone-700 hover:bg-stone-600 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
              >
                <Download className="w-5 h-5" /> Ver/Baixar PDF
              </a>
              <button
                data-testid="send-whatsapp-btn"
                onClick={handleSendWhatsApp}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-xl flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" /> Enviar pelo WhatsApp
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuoteBuilder;
